import { ExclamationCircleOutlined } from '@ant-design/icons';
import { Col, Modal, Row, Tag, notification } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router';
import { useGetMappingDetails } from '../../../../utils/api/mapping/useGetMappingDetails';
import { useMapProducts } from '../../../../utils/api/mapping/useMapProducts';
import { usePreviewMapping } from '../../../../utils/api/mapping/usePreviewMapping';
import { useGetMarketplaces } from '../../../../utils/api/marketplaces/useGetMarketplaces';
import { useGetMpAttributes } from '../../../../utils/api/mpAttributes/useGetMpAttributes';
import { useGetMpCategorieAttributes } from '../../../../utils/api/mpCategories/useGetMpCategorieAttributes';
import { useGetRawAttributes } from '../../../../utils/api/mpAttributes/useGetRawAttributes';
import { formatToUI, getRequirementInfos } from '../../../../utils/fn/mapping';
import {
  useMappingDetails,
  useRawAttributes,
  useSelectedField,
} from '../../../../utils/zustand';
import NestedDrawers from '../configurator/drawer';
import PreviewDrawer from '../preview/PreviewDrawer';
import AttributeFilters from './AttributeFilters';
import AttributeTable from './AttributeTable';
import { getAttributeColumns } from './columns';
import { useEnrichedAttributeList } from '../../../../utils/zustand/hooks/useEnrichedAttributeList';

const AttributeList = () => {
  const { mappingId } = useParams();
  const [search, setSearch] = useState('');
  const [filterLang, setFilterLang] = useState(null);
  const [filterType, setFilterType] = useState(null);
  const [filterRequirement, setFilterRequirement] = useState(null);
  const [drawerTrigger, setDrawerTrigger] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewData, setPreviewData] = useState(null);

  // API CALLS
  const { mappingData } = useGetMappingDetails(mappingId);
  const { mapProducts, isLoading: isMapProductsLoading } = useMapProducts();
  const {
    previewMapping,
    isLoading: isPreviewLoading,
    error: previewError,
  } = usePreviewMapping();
  // TODO PERF: Modifier le backend pour que /mappings/{id}/ renvoie marketplace_id
  // dans mp_category, afin d'éviter cette requête supplémentaire
  const { marketplaces } = useGetMarketplaces(
    { name: mappingData?.mp_category?.marketplace_name },
    { enabled: !!mappingData?.mp_category?.marketplace_name }
  );

  // ZUSTAND STORE
  const { mappingDetails, setMappingDetails } = useMappingDetails();
  const { setRawAttributes } = useRawAttributes();
  const { setSelectedField } = useSelectedField();

  const { rawAttributesDatas } = useGetRawAttributes(
    mappingDetails?.mp_category?.id
  );
  // Sync fetched mapping details to Zustand store
  useEffect(() => {
    if (mappingId && mappingData) {
      setMappingDetails(mappingData);
    }
  }, [mappingId, mappingData, setMappingDetails]);

  // Sync fetched mapping details to Zustand store
  useEffect(() => {
    if (rawAttributesDatas) {
      setRawAttributes(rawAttributesDatas);
    }
  }, [rawAttributesDatas, setRawAttributes]);

  const mpCategoryId = useMemo(
    () => mappingDetails?.mp_category?.id || '',
    [mappingDetails]
  );

  const { mpAttributes, isLoading: _isMpAttributesLoading } =
    useGetMpAttributes(
      {
        categories__mp_category__id: mpCategoryId,
        categories__selected: true,
      },
      { enabled: !!mpCategoryId }
    );

  // Store-aware attribute values: a mapping targets a single store, so we
  // resolve list values (e.g. Amazon unit_count, localized enums) for that
  // store/category via the dedicated endpoint and override the global ones.
  const { categorieAttributes } = useGetMpCategorieAttributes({
    mpCategoryId,
    store: mappingDetails?.store?.id,
    options: { enabled: !!mpCategoryId && !!mappingDetails?.store?.id },
  });

  // Store-aware attribute data resolved by the backend for this mapping's store:
  //  - values: localized / category-specific list values (Amazon unit_count,
  //    Mirakl Home24 locale-specific lists)
  //  - name: localized attribute label (Mirakl Home24)
  // For marketplaces flagged `has_per_store_attributes` (Home24), this endpoint
  // also restricts the attribute set to those present for the store, so we use
  // its codes to gate which attributes are offered.
  const storeAttrByCode = useMemo(() => {
    const map = {};
    (categorieAttributes || []).forEach((attr) => {
      map[attr.code] = {
        name: attr?.name,
        values: attr?.metadata?.values,
      };
    });
    return map;
  }, [categorieAttributes]);

  // `null` while loading -> no gating yet (avoids hiding everything). Once
  // loaded, only codes present for the store are kept among unmapped ones.
  const storeCodes = useMemo(
    () =>
      categorieAttributes
        ? new Set(categorieAttributes.map((attr) => attr.code))
        : null,
    [categorieAttributes]
  );

  // TODO: optim this with custom hook
  const {
    enrichedAttributeList,
    setEnrichedAttributeList,
    isLoading: enrichedAttributeListIsLoaded,
  } = useEnrichedAttributeList();
  useEffect(() => {
    if (
      !mappingDetails ||
      !mpAttributes ||
      !storeAttrByCode ||
      !storeCodes ||
      !rawAttributesDatas
    )
      setEnrichedAttributeList(null);
    else {
      const mappingConfig = mappingDetails.transformations;
      const mappingAttributes = formatToUI(mappingConfig);

      // Enrich mapped attributes with requirement info from mpAttributes
      const enrichedmappingAttributes = mappingAttributes.map((data) => {
        const mpAttr = mpAttributes?.find(
          (attr) => attr?.code === data?.target_field
        );
        const storeAttr = storeAttrByCode[data?.target_field];

        return {
          ...data,
          requirement: getRequirementInfos(mpAttr?.requirement),
          target_field_label: storeAttr?.name ?? mpAttr?.name,
          format: mpAttr?.format,
          description: mpAttr?.description,
          mpListValues: storeAttr?.values ?? mpAttr?.values_list,
          technicalAttributesValues: rawAttributesDatas,
        };
      });

      // Get already mapped target fields
      const mappedTargetFields = new Set(
        mappingAttributes.map((data) => data.target_field)
      );

      // Transform mpAttributes to match the table format (only unmapped ones).
      // For per-store marketplaces (Home24), `/mp_attributes/` returns the union
      // across stores, so we keep only the attributes present for this mapping's
      // store (storeCodes). When the store-aware data isn't loaded yet
      // (storeCodes === null), no gating is applied.
      const unmappedMpAttributes = (mpAttributes || [])
        .filter((attr) => !mappedTargetFields.has(attr.code))
        .filter((attr) => !storeCodes || storeCodes.has(attr.code))
        .map((attr) => {
          const storeAttr = storeAttrByCode[attr.code];
          return {
            key: `mp-attr-${attr.id}`,
            target_field: attr.code,
            target_field_label: storeAttr?.name ?? attr.name,
            source_field: null,
            transformation_type: 'none',
            lang: 'fr',
            requirement: getRequirementInfos(attr.requirement),
            format: attr.format,
            description: attr.description,
            mpListValues: storeAttr?.values ?? attr.values_list,
            technicalAttributesValues: rawAttributesDatas,
          };
        });

      // Merge both lists
      const allAttributes = [
        ...enrichedmappingAttributes,
        ...unmappedMpAttributes,
      ];

      const searchLower = search.toLowerCase();
      setEnrichedAttributeList(
        allAttributes
          .filter(
            (data) =>
              data?.target_field?.toLowerCase().includes(searchLower) ||
              data?.target_field_label?.toLowerCase().includes(searchLower)
          )
          .filter((data) => !filterLang || data.lang === filterLang)
          .filter(
            (data) => !filterType || data.transformation_type === filterType
          )
          .filter(
            (data) =>
              !filterRequirement || data.requirement.name === filterRequirement
          )
      );
    }
  }, [
    mappingDetails,
    mpAttributes,
    storeAttrByCode,
    storeCodes,
    rawAttributesDatas,
    search,
    filterLang,
    filterType,
    filterRequirement,
  ]);

  const handleTransformAll = () => {
    const marketplaceId = marketplaces?.[0]?.id;

    Modal.confirm({
      title: 'Lancer la transformation',
      icon: <ExclamationCircleOutlined />,
      content: `Voulez-vous transformer tous les produits de la catégorie "${mappingDetails?.mp_category?.name || ''}" ?`,
      okText: 'Transformer',
      cancelText: 'Annuler',
      onOk: async () => {
        if (!marketplaceId) {
          notification.error({
            message: 'Erreur',
            description: 'Impossible de déterminer la marketplace.',
            placement: 'bottomRight',
            duration: 3,
          });
          return;
        }
        const mp_categories = [mappingDetails?.mp_category?.id];
        const stores = [mappingDetails?.store?.id];
        const body = { mp_categories, stores };
        try {
          await mapProducts({ marketplaceId, body });
          notification.success({
            message: 'Transformation lancée',
            description:
              'La transformation des produits a été lancée avec succès.',
            placement: 'bottomRight',
            duration: 3,
          });
        } catch (error) {
          notification.error({
            message: 'Erreur de transformation',
            description:
              error?.response?.data?.detail ||
              'Une erreur est survenue lors de la transformation des produits.',
            placement: 'bottomRight',
            duration: 3,
          });
        }
      },
    });
  };

  const handlePreviewMapping = async () => {
    if (!mappingId) return;
    try {
      const data = await previewMapping({ mappingId });
      setPreviewData(data);
      setPreviewOpen(true);
    } catch (error) {
      notification.error({
        message: 'Erreur de prévisualisation',
        description: error?.response?.data?.detail || 'Une erreur est survenue',
        placement: 'bottomRight',
        duration: 3,
      });
    }
  };

  const handleClosePreview = () => {
    setPreviewOpen(false);
    setPreviewData(null);
  };

  const handleRowClick = (record) => {
    setSelectedField(record);
    setDrawerTrigger({ level: 0, nonce: Date.now() });
  };

  const onReferenceChange = async (reference) => {
    if (!mappingId) return;
    try {
      const data = await previewMapping({ mappingId, product: reference });
      setPreviewData(data);
    } catch (error) {
      notification.error({
        message: 'Erreur de prévisualisation',
        description: error?.response?.data?.detail || 'Une erreur est survenue',
        placement: 'bottomRight',
        duration: 3,
      });
    }
  };

  const columns = useMemo(
    () => getAttributeColumns(rawAttributesDatas),
    [rawAttributesDatas]
  );

  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          <h1 style={{ margin: 0 }}>Mapping</h1>
          <Tag color="blue" style={{ fontSize: 14 }}>
            {enrichedAttributeList?.length} attribut
            {enrichedAttributeList?.length > 1 ? 's' : ''}
          </Tag>
          {mappingDetails?.store?.name && (
            <span>
              <strong>Store :</strong> {mappingDetails.store.name}
            </span>
          )}
          {mappingDetails?.mp_category?.name && (
            <span>
              <strong>Catégorie MP :</strong> {mappingDetails.mp_category.name}
            </span>
          )}
        </div>
      </Col>
      <Col span={24}>
        <AttributeFilters
          search={search}
          setSearch={setSearch}
          filterLang={filterLang}
          setFilterLang={setFilterLang}
          filterType={filterType}
          setFilterType={setFilterType}
          filterRequirement={filterRequirement}
          setFilterRequirement={setFilterRequirement}
          onTransformAll={handleTransformAll}
          onPreviewMapping={handlePreviewMapping}
          isTransformDisabled={!mappingId}
          isPreviewDisabled={!mappingId}
          isTransformLoading={isMapProductsLoading}
          isPreviewLoading={isPreviewLoading}
        />
        <AttributeTable
          loading={enrichedAttributeListIsLoaded}
          columns={columns}
          dataSource={enrichedAttributeList}
          onRowClick={handleRowClick}
        />
        <NestedDrawers triggerOpen={drawerTrigger} />
        <PreviewDrawer
          open={previewOpen}
          onClose={handleClosePreview}
          onReferenceChange={onReferenceChange}
          store={mappingDetails?.store}
          mp_category={mappingDetails?.mp_category}
          data={previewData}
          isLoading={isPreviewLoading}
          error={previewError}
        />
      </Col>
    </Row>
  );
};

export default AttributeList;
